import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import axiosInstance from 'api/config/axios';
import { ProductPropertyTypeCode, TradeStatusAttributeCode } from 'utils/enums';
import { memoryQueryClient } from 'providers/TanstackQuery';
import { useDataValues } from 'providers/DataValues';
import { AimeosEntityType, AimeosManyResponse, AimeosProduct, AimeosProductType } from '../../types';
import { parseManyResponse } from '../../parsers';

type Params = {
  userId: string;
  pendingFundingAttrId: string;
};

async function getMyApprovedTrades({ userId, pendingFundingAttrId }: Params) {
  const params = {
    sort: 'product.mtime',
    fields: {
      product: 'product.id,product.mtime,product.code',
    },
    filter: {
      '&&': [
        { '==': { 'product.status': 1 } },
        { '==': { 'product.type': AimeosProductType.Trade } },
        { '!=': { [`product:prop("${ProductPropertyTypeCode.TradeCreator}",null,"${userId}")`]: null } },
        { '!=': { [`product:has("attribute","default","${pendingFundingAttrId}")`]: null } },
      ],
    },
  };

  const response = await axiosInstance.query.get<AimeosManyResponse<AimeosProduct>>('/admin/default/jsonadm/product', {
    params,
  });

  return response.data;
}

const selector = (result: AimeosManyResponse<AimeosProduct>) => {
  return parseManyResponse<AimeosEntityType.Product>(result).map((item) => {
    const code = item.data.code;
    return {
      ...item,
      data: {
        ...item.data,
        code: code.includes('-trade') ? (code.split('-trade')?.[0] ?? code) : code,
      },
    };
  });
};

export const getQueryKey = (params: Params) =>
  ['trade', 'approved-trades', params.userId, params.pendingFundingAttrId] as const;

export function useGetMyApprovedTrades(enabled = true) {
  const { userId, appValues } = useDataValues();

  const pendingFundingAttrId = useMemo(
    () => appValues?.attributes?.tradeStatus?.find((a) => a.code === TradeStatusAttributeCode.PendingFunding)?.id ?? '',
    [appValues],
  );

  const params = useMemo(() => ({ userId, pendingFundingAttrId }), [userId, pendingFundingAttrId]);

  return useQuery(
    {
      queryKey: getQueryKey(params),
      queryFn: () => getMyApprovedTrades(params),
      enabled: !!params.userId && !!params.pendingFundingAttrId && enabled,
      select: selector,
    },
    memoryQueryClient,
  );
}
